import IonIcon from '@reacticons/ionicons'
import cn from 'classnames'
import Link from 'next/link'
import React, { useState } from 'react'
import { PremiumTag } from 'src/components/global/PremiumTag/PremiumTag'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { INITIAL_ITEM_CNT } from 'src/config/config'
import { FilterOptionItem, TaxonomyType } from 'src/types'

interface SidebarTopicFilterProps {
  title: TaxonomyType.Topic | TaxonomyType.Subtopic
  topicSlug: string
  items: FilterOptionItem[]
  opened: boolean
  disabled?: boolean
  showCheckbox?: boolean
  onSelectAccordion: (title: TaxonomyType) => void
  onClickWhenDisabled?: () => void
}

export const SidebarTopicFilter: React.FC<SidebarTopicFilterProps> = ({
  title,
  topicSlug,
  items,
  opened,
  disabled = false,
  showCheckbox = false,
  onSelectAccordion,
  onClickWhenDisabled,
}) => {
  const [showAll, setShowAll] = useState(false)
  const handleShowAll = () => {
    if (disabled) {
      onClickWhenDisabled && onClickWhenDisabled()
      return
    }
    setShowAll(!showAll)
  }
  const toggleAccordion = () => {
    if (disabled) {
      onClickWhenDisabled && onClickWhenDisabled()
      return
    }
    onSelectAccordion(title)
  }

  const sortItems = () => {
    return items.sort((a, b) => a.name.localeCompare(b.name)).sort((a) => (a.isPremium ? -1 : 1))
  }

  return (
    <div className="w-full flex flex-col">
      <button
        className="py-3 box-border appearance-none cursor-pointer focus:outline-none flex items-center justify-between"
        onClick={toggleAccordion}
      >
        <p className="inline-block text-footnote light font-bold font-primary text-textdark uppercase text-[0.94rem]">
          <ThemeOnly theme="legaltech">{title === TaxonomyType.Topic ? 'Topics' : 'Products/Services'}</ThemeOnly>
          <ThemeOnly theme="ilta">{title === TaxonomyType.Topic ? 'Topics' : 'Sub-Topics'}</ThemeOnly>
        </p>
        {opened ? <IonIcon name="chevron-up-outline" /> : <IonIcon name="chevron-down-outline" />}
      </button>
      <div
        style={{ maxHeight: opened ? `380px` : '0px' }}
        className="transition-max-height duration-500 overflow-y-scroll ease-in-out"
      >
        {sortItems().map(({ name, slug, isPremium }, index: number) => {
          let clsName = 'pb-4 pl-[0.5rem] font-primary text-[0.94rem] flex flex-row items-center justify-between'
          if (index === 0) {
            clsName += ' mt-[12px]'
          } else if (index === items.length - 1) {
            clsName += ' mb-[12px]'
          }
          if (index < INITIAL_ITEM_CNT) {
            return (
              <div key={index} className={clsName}>
                {showCheckbox && <input disabled={disabled} type="checkbox" id={title + '-' + index} />}
                <Link href={title === TaxonomyType.Topic ? `/topics/${slug}` : `/topics/${topicSlug}/${slug}`} passHref>
                  {name}
                </Link>
                <ThemeOnly theme="legaltech">
                  {isPremium && (
                    <div>
                      <PremiumTag className="ml-[10px] text-[0.8125rem]" />
                    </div>
                  )}
                </ThemeOnly>
              </div>
            )
          } else {
            if (showAll) {
              return (
                <div key={index} className={cn(`${clsName} flex flex-row items-center justify-between`)}>
                  {showCheckbox && <input disabled={disabled} type="checkbox" id={title + '-' + index} />}
                  <Link
                    href={title === TaxonomyType.Topic ? `/topics/${slug}` : `/topics/${topicSlug}/${slug}`}
                    passHref
                  >
                    {name}
                  </Link>
                  <ThemeOnly theme="legaltech">
                    {isPremium && (
                      <div>
                        <PremiumTag className="ml-[10px] text-[0.8125rem]" />
                      </div>
                    )}
                  </ThemeOnly>
                </div>
              )
            } else {
              return <div key={index}></div>
            }
          }
        })}
        {items && items.length > INITIAL_ITEM_CNT && !showAll && (
          <div
            className="pb-4 pl-[0.5rem] font-primary text-textdark text-[0.94rem] underline cursor-pointer"
            onClick={handleShowAll}
          >
            +{items.length - INITIAL_ITEM_CNT} {'more'}
          </div>
        )}
      </div>
    </div>
  )
}
